import { TOKEN_EXPIRY } from '@constants/auth.constants';
import { LocalStorageKeys, SessionStorageKeys } from '@constants/enums';
import { clearProfile } from '@pages/profile/store';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { authState } from '../../layout/login/auth';
import { clearAuthHeaders } from '../config';
import {
  clearInvalidAuth,
  getAuthToken,
  isLegacyAuth,
  safeParseJSON,
} from './authUtils';
import storageService from './storageService';
import type { CognitoAuthData, LegacyAuthResponse } from './types';

function hydrateLegacyAuth(token: string, dispatch: any) {
  const roles = safeParseJSON(
    localStorage.getItem(SessionStorageKeys.ROLES),
    []
  );
  const refresh = localStorage.getItem(LocalStorageKeys.REFRESH);
  const userKyc = safeParseJSON(
    localStorage.getItem(LocalStorageKeys.USER_KYC),
    false
  );

  if (!Array.isArray(roles) || roles.length === 0) {
    clearInvalidAuth();
    return;
  }

  const authData: LegacyAuthResponse = {
    isActive: true,
    isFullRegister: true,
    msg: null,
    token,
    refresh,
    user_kyc: userKyc,
    is_email_confirmed: true,
  };

  dispatch(authState.actions.authenticate(authData));
}

function hydrateCognitoAuth(token: string, dispatch: any) {
  if (!storageService.hasValidSession()) {
    clearInvalidAuth();
    return;
  }

  const roles = storageService.getRoles();
  const permissions = storageService.getPermissions();
  const refreshToken = storageService.getRefreshToken();
  const accessToken = storageService.getAccessToken();

  if (!roles.length || !refreshToken || !accessToken) {
    clearInvalidAuth();
    return;
  }

  const authData: CognitoAuthData = {
    idToken: token,
    refreshToken,
    accessToken,
    expiresIn: TOKEN_EXPIRY.DEFAULT_SECONDS,
    tokenType: 'Bearer',
    roles,
    permissions,
  };

  dispatch(authState.actions.cognitoAuthenticate(authData));
}

const clearNewWindowStorage = (): void => {
  const onboardingData = localStorage.getItem(
    LocalStorageKeys.ONBOARDING_PROCESS_DATA
  );
  const visitCount = localStorage.getItem(LocalStorageKeys.VISIT_COUNT);
  const doNotShowConvert = localStorage.getItem(
    LocalStorageKeys.DO_NOT_SHOW_CONVERT
  );

  localStorage.clear();
  sessionStorage.clear();

  clearAuthHeaders();

  if (onboardingData) {
    localStorage.setItem(
      LocalStorageKeys.ONBOARDING_PROCESS_DATA,
      onboardingData
    );
  }
  if (visitCount) {
    localStorage.setItem(LocalStorageKeys.VISIT_COUNT, visitCount);
  }
  if (doNotShowConvert) {
    localStorage.setItem(
      LocalStorageKeys.DO_NOT_SHOW_CONVERT,
      doNotShowConvert
    );
  }
};

export const useAuthHydration = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const hasCleanedStorage = useRef(false);
  const isInitialized = useRef(false);

  const isLoginPage = location.pathname === '/login';

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const token = getAuthToken();
    const roles = sessionStorage.getItem(SessionStorageKeys.ROLES);

    if (!roles && isLoginPage && !hasCleanedStorage.current) {
      hasCleanedStorage.current = true;
      clearNewWindowStorage();
      dispatch(clearProfile());
      dispatch(authState.actions.logout());
      return;
    }

    if (!token) {
      dispatch(authState.actions.setHydrationCompleted(true));
      return;
    }

    try {
      if (isLegacyAuth()) {
        hydrateLegacyAuth(token, dispatch);
      } else {
        hydrateCognitoAuth(token, dispatch);
      }
    } catch (error) {
      console.error('Auth hydration failed:', error);
      clearInvalidAuth();
    }

    dispatch(authState.actions.setHydrationCompleted(true));
  }, [dispatch, isLoginPage]);

  useEffect(() => {
    if (!isLoginPage) {
      hasCleanedStorage.current = false;
    }
  }, [isLoginPage]);
};

export default useAuthHydration;
